import { Injectable } from '@angular/core';
import { ICostUnitGroupOverview } from '../../../models/cost-unit-group-overview.model';

import { BehaviorSubject, finalize, firstValueFrom, Observable, of, tap } from 'rxjs';
import { IContributionMarginLevel } from '../../../models/contribution-margin-level.model';
import { ICostUnitGroup } from '../../../models/cost-unit-group.model';
import { ICostUnit } from '../../../models/cost-unit.model';
import { ContributionMarginLevelResource } from '../../../resources/contribution-margin-level.resource';

import { CostUnitGroupResource } from '../../../resources/cost-unit-group.resource';
import { CostUnitResource } from '../../../resources/cost-unit.resource';
import { ICostUnitGroupState } from './cost-unit-group-state.model';
import { catchError } from 'rxjs/operators';
import { MessageService } from '@sla-ng/core';

let _state: ICostUnitGroupState = {
    isLoadingAssignedContributionMarginLevels: false,
    isLoadingAssignedCostUnits: false,
    isLoading: false,
    contributionMarginLevels: [],
    costUnitGroups: [],
    costUnits: [],
    selectedCostUnitGroup: undefined,
    assignedContributionMarginLevels: [],
    assignedCostUnits: [],
};

@Injectable({
    providedIn: 'root',
})
export class CostUnitGroupFacade {

    protected store$: BehaviorSubject<ICostUnitGroupState> = new BehaviorSubject<ICostUnitGroupState>(_state);

    constructor(private myCostUnitGroupResource: CostUnitGroupResource,
                private myCostUnitResource: CostUnitResource,
                private myContributionMarginLevel: ContributionMarginLevelResource,
                private myMessageService: MessageService) {
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);
        const costUnitGroups: ICostUnitGroupOverview[] = await firstValueFrom(this.myCostUnitGroupResource.getAllCostUnitGroups()).catch(() => []);

        this.updateState({ ..._state, costUnitGroups });
        this.setIsloading(false);
    }

    public subState(): Observable<ICostUnitGroupState> {
        return this.store$.asObservable();
    }

    public fetchCostUnits(): Observable<ICostUnit[]> {
        if (_state.costUnits.length > 0) {
            return of(_state.costUnits);
        }
        this.setIsloading(true);
        return this.myCostUnitResource.getAllCostUnits()
            .pipe(
                tap((costUnits: ICostUnit[]) => this.updateState({ ..._state, costUnits })),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }

    public async deleteCostUnitGroup(costUnitGroupId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostUnitGroupResource.deleteCostUnitGroup(costUnitGroupId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public fetchContributionMarginLevels(): Observable<IContributionMarginLevel[]> {
        if (_state.contributionMarginLevels.length > 0) {
            return of(_state.contributionMarginLevels);
        }
        this.setIsloading(true);
        return this.myContributionMarginLevel.getAllContributionMarginLevels()
            .pipe(
                tap((contributionMarginLevels: IContributionMarginLevel[]) => this.updateState({ ..._state, contributionMarginLevels })),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }

    public getStateSnapshot(): ICostUnitGroupState {
        return this.store$.value;
    }

    public async createCostUnitGroup(costUnitGroup: ICostUnitGroup): Promise<void> {
        const createdGroup: ICostUnitGroup = await firstValueFrom(this.myCostUnitGroupResource.createCostUnitGroup(costUnitGroup)).catch(() => null);
        if (createdGroup !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.actions.added.label');
        }
    }

    public async updateCostUnitGroup(costUnitGroup: ICostUnitGroup): Promise<void> {
        const updatedGroup: ICostUnitGroup = await firstValueFrom(this.myCostUnitGroupResource.updateCostUnitGroup(costUnitGroup)).catch(() => null);
        if (updatedGroup !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.actions.edited.label');
        }
    }

    public async fetchAssignedCostUnits(): Promise<ICostUnit[]> {
        this.setIsloadingAssignedCostUnits(true);
        const state: ICostUnitGroupState = this.getStateSnapshot();
        const assignedCostUnits: ICostUnit[] = await this.myCostUnitGroupResource.getAssignedCostUnitsForGroup(state.selectedCostUnitGroup.id).toPromise().catch(() => []);
        this.updateState({ ..._state, assignedCostUnits });
        this.setIsloadingAssignedCostUnits(false);
        return assignedCostUnits;
    }

    public async fetchAssignedContributionMarginLevels(): Promise<void> {
        this.setIsloadingAssignedContributionMarginLevels(true);
        const state: ICostUnitGroupState = this.getStateSnapshot();
        const assignedContributionMarginLevels: IContributionMarginLevel[] = await this.myCostUnitGroupResource
            .getAssignedContributionMarginLevelsForGroup(state.selectedCostUnitGroup.id).toPromise().catch(() => []);
        this.updateState({ ..._state, assignedContributionMarginLevels });
        this.setIsloadingAssignedContributionMarginLevels(false);
    }

    public async assignCostUnitToGroup(costUnits: number[]): Promise<ICostUnit[]> {
        this.setIsloadingAssignedCostUnits(true);
        let assignedCostUnits: ICostUnit[] = null;
        try {
            await firstValueFrom(this.myCostUnitGroupResource.assignCostUnitToGroup(_state.selectedCostUnitGroup.id, costUnits));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.assigned-cost-units.actions.added.label');
            assignedCostUnits = await this.fetchAssignedCostUnits();
        } catch (e) {
            console.error(e);
        }
        this.setIsloadingAssignedCostUnits(false);
        return assignedCostUnits;
    }

    public async assignContributionMarginLevelToGroup(contributionMarginLevelId: number): Promise<void> {
        this.setIsloadingAssignedContributionMarginLevels(true);
        const state: ICostUnitGroupState = this.getStateSnapshot();
        try {
            await firstValueFrom(this.myCostUnitGroupResource.assignContributionMarginLevelToGroup(state.selectedCostUnitGroup.id, contributionMarginLevelId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.assigned-cml.actions.added.label');
            await this.fetchAssignedContributionMarginLevels();
        } catch (e) {
            console.error(e);
        }
        this.setIsloadingAssignedCostUnits(false);
    }

    public async setSelectedCostUnitGroup(costUnitGroup: ICostUnitGroup): Promise<void> {
        this.updateState({ ..._state, selectedCostUnitGroup: costUnitGroup });
        await this.fetchAssignedCostUnits();
        await this.fetchAssignedContributionMarginLevels();
    }

    public clearState(): void {
        this.updateState({
            isLoadingAssignedContributionMarginLevels: false,
            isLoadingAssignedCostUnits: false,
            isLoading: false,
            contributionMarginLevels: [],
            costUnitGroups: [],
            costUnits: [],
            selectedCostUnitGroup: undefined,
            assignedContributionMarginLevels: [],
            assignedCostUnits: [],
        });
    }

    public async deleteCostUnitAssignment(id: number): Promise<void> {
        this.setIsloadingAssignedCostUnits(true);
        const state: ICostUnitGroupState = this.getStateSnapshot();
        try {
            await firstValueFrom(this.myCostUnitGroupResource.removeCostUnitAssignment(state.selectedCostUnitGroup.id, id));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.assigned-cost-units.actions.deleted.label');
            await this.fetchAssignedCostUnits();
        } catch (e) {
            console.error(e);
        }
        this.setIsloadingAssignedCostUnits(false);
    }

    public async deleteContributionMarginLevelAssignment(id: number): Promise<void> {
        this.setIsloadingAssignedContributionMarginLevels(true);
        const state: ICostUnitGroupState = this.getStateSnapshot();
        try {
            await firstValueFrom(this.myCostUnitGroupResource.unassignContributionMarginLevel(state.selectedCostUnitGroup.id, id));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-unit-group.assigned-cml.actions.deleted.label');
            await this.fetchAssignedContributionMarginLevels();
        } catch (e) {
            console.error(e);
        }
        this.setIsloadingAssignedContributionMarginLevels(false);
    }

    public async fetchCostUnitGroups(): Promise<void> {
        this.setIsloading(true);
        const costUnitGroups: ICostUnitGroupOverview[] = await this.myCostUnitGroupResource.getAllCostUnitGroups().toPromise().catch(() => []);
        this.updateState({ ..._state, costUnitGroups });
        this.setIsloading(false);
    }

    private updateState(state: ICostUnitGroupState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    private setIsloadingAssignedCostUnits(isLoading: boolean): void {
        this.updateState({ ..._state, isLoadingAssignedCostUnits: isLoading });
    }

    private setIsloadingAssignedContributionMarginLevels(isLoading: boolean): void {
        this.updateState({ ..._state, isLoadingAssignedContributionMarginLevels: isLoading });
    }

}
